import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { timeEntryService } from '../services/timeEntryService';
import { supabase } from '../lib/supabase';
import { getProjectBillable } from '../services/projectBillingService';

interface KanbanTimeTrackingContextType {
  // Rastreamento genérico (toggle simples usado no modal de criar/editar tarefa,
  // não vinculado a uma tarefa específica nem persistido)
  isTrackingTime: boolean;
  trackingStartTime: Date | null;
  toggleTimeTracking: () => void;

  // Rastreamento por card do Kanban (persistido no Supabase)
  trackingTaskId: string | null;
  taskTrackingTimes: Record<string, Date>;
  // Hora de início da sessão ativa, independente de sabermos a qual card ela
  // pertence — necessário porque `time_entries.activity_id` tem uma FK para a
  // tabela legada `activities`, que não tem relação com as tasks do Kanban
  // (`project_kanban_tasks`), então não conseguimos gravar o id da task ali
  // nem recuperá-lo depois de um refresh.
  activeStartTime: Date | null;
  showTrackingPopup: boolean;
  setShowTrackingPopup: (show: boolean) => void;
  isTrackingPopupMinimized: boolean;
  setIsTrackingPopupMinimized: (minimized: boolean) => void;
  currentTrackedTaskTitle: string;
  toggleTaskTimeTracking: (taskId: string, taskTitle: string) => Promise<void>;
  startTaskTracking: (taskId: string, taskTitle: string) => Promise<void>;
  stopTaskTracking: () => Promise<void>;

  // Usado pela tela de Atividades para informar qual projeto está selecionado,
  // já que o contexto é global e não recebe isso via prop.
  setActiveProjectId: (projectId: string) => void;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const KanbanTimeTrackingContext = createContext<KanbanTimeTrackingContextType | null>(null);

export const KanbanTimeTrackingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { organization } = useOrganization();

  const [isTrackingTime, setIsTrackingTime] = useState(false);
  const [trackingStartTime, setTrackingStartTime] = useState<Date | null>(null);

  const [trackingTaskId, setTrackingTaskId] = useState<string | null>(null);
  const [taskTrackingTimes, setTaskTrackingTimes] = useState<Record<string, Date>>({});
  const [activeStartTime, setActiveStartTime] = useState<Date | null>(null);
  const [showTrackingPopup, setShowTrackingPopup] = useState(false);
  const [isTrackingPopupMinimized, setIsTrackingPopupMinimized] = useState(false);
  const [currentTrackedTaskTitle, setCurrentTrackedTaskTitle] = useState('');

  const activeProjectIdRef = useRef<string>('');
  const setActiveProjectId = useCallback((projectId: string) => {
    activeProjectIdRef.current = projectId;
  }, []);

  // Ao carregar (login/refresh), recupera um rastreamento em andamento gravado
  // no Supabase, caso a aba tenha sido recarregada com um timer ativo.
  useEffect(() => {
    if (!user?.id) return;

    timeEntryService.getCurrentTimeEntry(user.id).then(entry => {
      if (entry) {
        // Não sabemos a qual card do Kanban esta entrada pertence (ver nota em
        // `activeStartTime`), então o card em si não fica marcado como "em
        // rastreamento" — mas o popup global reabre com o tempo já decorrido.
        setCurrentTrackedTaskTitle(entry.description || '');
        setActiveStartTime(new Date(entry.start_time));
        setShowTrackingPopup(true);
      }
    }).catch(err => {
      console.error('Erro ao recuperar rastreamento em andamento:', err);
    });
  }, [user?.id]);

  const toggleTimeTracking = useCallback(() => {
    setIsTrackingTime(prev => {
      if (prev) {
        setTrackingStartTime(null);
        return false;
      }
      setTrackingStartTime(new Date());
      return true;
    });
  }, []);

  const startTaskTracking = useCallback(async (taskId: string, taskTitle: string) => {
    if (!user?.id) return;

    try {
      // `activity_id` não é enviado: tem FK para a tabela legada `activities`,
      // que não guarda as tasks do Kanban — enviar o id da task aqui derrubaria
      // o insert com violação de foreign key.
      // O projeto vem da própria atividade, para as horas caírem no projeto certo
      // mesmo que o filtro da tela aponte para outro.
      // O modal de nova atividade rastreia com um id provisório, que não é uuid.
      const isSavedTask = UUID_RE.test(taskId);
      const { data: task } = isSavedTask
        ? await supabase.from('project_kanban_tasks').select('project_id').eq('id', taskId).maybeSingle()
        : { data: null };
      const projectId = task?.project_id || activeProjectIdRef.current || undefined;
      const entry = await timeEntryService.startTimeTracking(user.id, organization?.id || '', {
        description: taskTitle,
        project_id: projectId,
        kanban_task_id: isSavedTask ? taskId : undefined,
        billable: projectId ? (await getProjectBillable(projectId)) ?? false : false
      });

      setTrackingTaskId(taskId);
      setCurrentTrackedTaskTitle(taskTitle);
      const startTime = new Date(entry.start_time);
      setTaskTrackingTimes({ [taskId]: startTime });
      setActiveStartTime(startTime);
      setShowTrackingPopup(true);
      setIsTrackingPopupMinimized(false);
    } catch (error) {
      console.error('Erro ao iniciar rastreamento:', error);
    }
  }, [user?.id, organization?.id]);

  const stopTaskTracking = useCallback(async () => {
    if (!user?.id || !activeStartTime) return;

    try {
      await timeEntryService.stopTimeTracking(user.id);
    } catch (error) {
      console.error('Erro ao parar rastreamento:', error);
    }

    setTaskTrackingTimes({});
    setActiveStartTime(null);
    setTrackingTaskId(null);
    setCurrentTrackedTaskTitle('');
    setShowTrackingPopup(false);
  }, [user?.id, activeStartTime]);

  const toggleTaskTimeTracking = useCallback(async (taskId: string, taskTitle: string) => {
    if (trackingTaskId === taskId) {
      await stopTaskTracking();
      return;
    }

    if (activeStartTime) {
      await stopTaskTracking();
    }

    await startTaskTracking(taskId, taskTitle);
  }, [trackingTaskId, activeStartTime, stopTaskTracking, startTaskTracking]);

  const value = useMemo(() => ({
    isTrackingTime,
    trackingStartTime,
    toggleTimeTracking,
    trackingTaskId,
    taskTrackingTimes,
    activeStartTime,
    showTrackingPopup,
    setShowTrackingPopup,
    isTrackingPopupMinimized,
    setIsTrackingPopupMinimized,
    currentTrackedTaskTitle,
    toggleTaskTimeTracking,
    startTaskTracking,
    stopTaskTracking,
    setActiveProjectId
  }), [
    isTrackingTime, trackingStartTime, toggleTimeTracking, trackingTaskId, taskTrackingTimes, activeStartTime, showTrackingPopup, setShowTrackingPopup, isTrackingPopupMinimized, setIsTrackingPopupMinimized, currentTrackedTaskTitle, toggleTaskTimeTracking, startTaskTracking, stopTaskTracking, setActiveProjectId
  ]);

  return (
    <KanbanTimeTrackingContext.Provider value={value}>
      {children}
    </KanbanTimeTrackingContext.Provider>
  );
};

export const useKanbanTimeTracking = () => {
  const context = useContext(KanbanTimeTrackingContext);
  if (!context) {
    throw new Error('useKanbanTimeTracking deve ser usado dentro de KanbanTimeTrackingProvider');
  }
  return context;
};
